import React, { Suspense } from 'react'
import { ReactReduxContext } from 'react-redux'
import PropTypes from 'prop-types'
import { schedulesActions } from '../actions/schedules'
import { checkModuleSetup, verifySchedules } from '../loader/verify-module'
import { ErrorBoundary } from './error-boundary'

/*
 * Factory to create a loadable module as defined by Modux contract. Modux loadable modules can expose Redux reducers
 * to be added to the Redux store at module load time. The returned Loadable React component replaces the existing
 * Redux store by combining initial reducers, all existing reducers and the reducers defined by the loaded module.
 *
 * It gets the promise returned by an import('<some module>') statement and uses @loadable/component to render it.
 * The actual import('<some module>') directives must be placed in the target application, so that Webpack will be
 * able to build appropriate bundles. This is because import('<some module>') directives are analyzed by Webpack at
 * build/bundle time.
 *
 * @param {Function} createReducer                   - Factory function to create the final Redux reducer
 * @param {Object} [translationsActions]             - Optional factory-created object with dispatchable translations
 *                                                     actions
 * @param {Function} importFn                        - Function, that returns the resulting promise of an
 *                                                     import(<some module>) directive
 * @param {React.Component} ErrorComponent           - Component to be rendered in case of errors during module
 *                                                     initialization
 * @param {React.Component} fallbackElement          - Element to be rendered as fallback during module loading
 * @returns {React.Component} A loadable component wrapped in an error boundary (fallback) component
 */
export function createLoadableComponent(createReducer, translationsActions, importFn, ErrorComponent, fallbackElement) {
    class LoadableComponent extends React.Component {
        constructor(props, context) {
            super(props, context)

            this.lazyComponent = React.lazy(async () => {
                const module = await importFn()
                const { store } = context
                const { View, contextCode, reducers, schedules } = module

                // Verify correct module setup
                checkModuleSetup(module)

                // Add schedules to the store
                if (schedules !== undefined) {
                    if (schedules instanceof Function) {
                        const effectiveSchedules = schedules(store.getState())
                        if (effectiveSchedules !== undefined) {
                            verifySchedules(effectiveSchedules)
                            store.dispatch(schedulesActions.patchSuccess(effectiveSchedules))
                        }
                    } else if (typeof schedules === 'object' && Object.keys(schedules).length > 0) {
                        store.dispatch(schedulesActions.patchSuccess(schedules))
                    }
                }

                // Fetch module translations and then add module-specific translation context code to the store
                if (typeof contextCode === 'string' && contextCode.length > 0 && translationsActions) {
                    store.dispatch(translationsActions.updateModuleTranslations(contextCode))
                }

                // React.lazy expects to return a promise, which resolves to a module with a default export
                // containg a React component
                const returnValue = {
                    default: View
                }

                if (reducers === undefined || typeof reducers !== 'object') {
                    return returnValue
                }

                // Hold the dynamically loaded reducers in Redux store
                store.loadableReducers = {
                    ...reducers,
                    ...store.loadableReducers
                }

                // Activate the new reducers
                store.replaceReducer(createReducer(store.loadableReducers))

                return returnValue
            })
        }

        render() {
            const LazyComponent = this.lazyComponent
            const { children, moduleProps } = this.props
            return (
                // Wrap module in an error boundary to avoid crashing of the whole ui in case of any exception in
                // the module code.
                <ErrorBoundary Fallback={ErrorComponent}>
                    <Suspense fallback={fallbackElement}>
                        <LazyComponent {...moduleProps}>{children}</LazyComponent>
                    </Suspense>
                </ErrorBoundary>
            )
        }
    }

    LoadableComponent.contextType = ReactReduxContext

    LoadableComponent.propTypes = {
        children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
        // eslint-disable-next-line react/forbid-prop-types
        moduleProps: PropTypes.object
    }

    if (ErrorComponent == null) {
        throw new Error('No Error fallback provided!')
    }

    if (fallbackElement == null) {
        throw new Error('No fallback element provided!')
    }

    return LoadableComponent
}

/**
 * Higher order function for convenience to create the universal loadable module factory above. It basically binds the
 * final reducers factory and the optional translations actions to the createLoadableComponent factory, so that both
 * parameters won't be transported all over the application. The reducer factory function should be created via
 * `createReducerFactory` from `reducer/reducer.js`. The translations actions should be created via
 * `createTranslationsActions` from `actions/intl.js` if the application requires internationalisation support.
 *
 * @param {Function} createReducer       - Factory function to create the final Redux reducer
 * @param {Object} [translationsActions] - Optional factory-created object with dispatchable translations actions
 * @return {Function} The pre-configured loader factory above
 */
export function createLoadableComponentFactory(createReducer, translationsActions) {
    return createLoadableComponent.bind(null, createReducer, translationsActions)
}
